import { useState, useEffect } from 'react';

/**
 * Calculate a value based on a base dimension
 * @param {string|number} value - Value to calculate (can be percentage string or number)
 * @param {number} baseDimension - Base dimension to calculate from
 * @returns {number} Calculated value
 */
export const calculateValue = (value, baseDimension) => (typeof value === 'string' && value.endsWith('%') ? (parseFloat(value) / 100) * baseDimension : value);

/**
 * Get path values based on dimensions
 * @param {Object} path - Path object with x, y, width, and/or height
 * @param {Object} dimensions - Container dimensions
 * @returns {Object} Calculated path values
 */
export const getPathValues = (path, dimensions) => {
	const x = calculateValue(path.x, dimensions.width);
	const y = calculateValue(path.y, dimensions.height);

	// Always use width to determine size, ensuring squares
	const size = path.width ? (calculateValue(path.width, dimensions.width) / dimensions.width) * 100 : (calculateValue(path.height, dimensions.height) / dimensions.height) * 100;

	return {
		x,
		y,
		width: size,
		height: size, // Use same value as width to ensure squares
	};
};

/**
 * Custom hook for calculating frame dimensions
 * @param {React.RefObject} ref - Reference to the container element
 * @param {Object} imageDimensions - Image dimensions (optional)
 * @returns {Object} Calculated dimensions
 */
export const useFrameDimensions = (ref, imageDimensions) => {
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

	useEffect(() => {
		const updateDimensions = () => {
			if (ref.current) {
				const container = ref.current.getBoundingClientRect();
				const imgRatio = imageDimensions ? imageDimensions.width / imageDimensions.height : 1.5;
				const containerRatio = container.width / container.height;

				let width, height;
				if (containerRatio > imgRatio) {
					height = container.height;
					width = height * imgRatio;
				} else {
					width = container.width;
					height = width / imgRatio;
				}

				setDimensions({
					width,
					height,
					left: (container.width - width) / 2,
					top: (container.height - height) / 2,
				});
			}
		};

		updateDimensions();
		window.addEventListener('resize', updateDimensions);
		return () => window.removeEventListener('resize', updateDimensions);
	}, [ref, imageDimensions]);

	return dimensions;
};

/**
 * Generate a unique ID for clip paths
 * @returns {string} Unique ID
 */
export const generateUniqueId = () => `clip-${Math.random().toString(36).substr(2, 9)}`;
